/**
 * SMÅKEN — the culinary map of Småland.
 *
 * Everything the public site renders is derived from this file. It is written
 * as flat array literals so it can later be swapped for a CMS/database without
 * touching a single component.
 *
 * Launch scope: Växjö, two partners. Other Småland towns are listed as
 * upcoming so visitors see where we're heading.
 */

import { CATERING, CateringItem, exVat, VAT_RATE } from './catering';

export type Kind = 'ata' | 'fika' | 'handla' | 'dricka' | 'upplev';

export interface KindMeta {
  id: Kind;
  label: string;
  sub: string;
  image: string;
  /** Editorial accent used for this category's colour block. */
  accent: string;
  ink: string;
}

export const KINDS: KindMeta[] = [
  {
    id: 'ata',
    label: 'ÄTA',
    sub: 'Restauranger & delis',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/00684a61-4f15-465c-9514-8a6b9f133b9b.jpg',
    accent: '#E42616',
    ink: '#F7F3EC',
  },
  {
    id: 'fika',
    label: 'FIKA',
    sub: 'Bagerier & caféer',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/264b58b6-1c66-481c-9dc4-a443e6930eeb.jpg',
    accent: '#F4C928',
    ink: '#111111',
  },
  {
    id: 'handla',
    label: 'HANDLA',
    sub: 'Producenter & gårdsbutiker',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/9f77d122-97ed-4274-88c9-691cf240cca3.jpg',
    accent: '#315C3A',
    ink: '#F7F3EC',
  },
  {
    id: 'dricka',
    label: 'DRICKA',
    sub: 'Kaffe, dryck & barer',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/73db5e3b-fdcd-4519-9a53-96913e9fba08.jpg',
    accent: '#2015B8',
    ink: '#F7F3EC',
  },
  {
    id: 'upplev',
    label: 'UPPLEV',
    sub: 'Events & upplevelser',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/6f8843ef-e486-4410-832d-1db1ed41e3f6.jpg',
    accent: '#8E2638',
    ink: '#F7F3EC',
  },
];

/** Positions on the stylised Småland map, as percentages of the viewBox. */
export const CITIES: Record<string, { x: number; y: number }> = {
  Jönköping: { x: 26, y: 16 },
  Eksjö: { x: 50, y: 24 },
  Vetlanda: { x: 52, y: 40 },
  Västervik: { x: 82, y: 26 },
  Oskarshamn: { x: 82, y: 45 },
  Växjö: { x: 41, y: 58 },
  Kalmar: { x: 79, y: 66 },
  Ljungby: { x: 22, y: 62 },
  Älmhult: { x: 30, y: 80 },
};

/** Where you can order today. */
export const ACTIVE_CITIES = ['Växjö'];

/** The rest of Småland — shown as "snart" so people know where we're heading. */
export const UPCOMING_CITIES = Object.keys(CITIES).filter((c) => !ACTIVE_CITIES.includes(c));

export interface Place {
  slug: string;
  name: string;
  city: keyof typeof CITIES | string;
  kind: Kind;
  /** Reads as the strap under the name: "Mackverk · Deli · Hantverksbak". */
  tags: string[];
  /** One confident sentence. This is the voice of the whole platform. */
  blurb: string;
  description: string[];
  /** "Det här gillar vi" — three specific, opinionated reasons. */
  weLike: string[];
  image: string;
  gallery: string[];
  address: string;
  hours: { day: string; time: string }[];
  price: 1 | 2 | 3;
  /** Editorial stamp shown on the card, e.g. "NYTT!" or "MÅSTE TESTAS". */
  stamp?: string;
  isNew?: boolean;
  /** Which action the profile page leads with. */
  action: 'boka' | 'bestall' | 'besok';
  instagram?: string;
  email?: string;
}

export type {
  Allergen,
  CateringCategory,
  CateringItem,
  CateringUnit,
  Diet,
  Occasion,
} from './catering';
export {
  ALLERGENS,
  CATERING_CATEGORIES,
  CATERING_UNITS,
  DIETS,
  OCCASIONS,
  VAT_RATE,
  exVat,
} from './catering';

export const PLACES: Place[] = [
  {
    slug: 'lilla-verket',
    name: 'LILLA VERKET',
    city: 'Växjö',
    kind: 'ata',
    tags: ['Mackverk', 'Deli', 'Hantverksbak'],
    blurb: 'Mackor med namn som får dig att le och fyllningar som får dig att tystna.',
    description: [
      'Lilla Verket är en plats där mat, vin och människor möts. Här står det lilla extra i centrum — från noggrant utvalda råvaror och hantverksbakat bröd till ost, chark och säsongens rätter.',
      'Till möten och event levererar de samma känsla: mackor som Fig & Pig och Beet & Bleat, matiga sallader och fika som inte är en eftertanke.',
    ],
    weLike: [
      'Fig & Pig. Taleggio och fikonmarmelad i samma tugga borde vara standard.',
      'Att salladerna faktiskt mättar. Ingen behöver smyga till kylskåpet vid tre.',
      'Basque cheesecake med pistage. Beställ en extra, den tar slut.',
    ],
    image: 'https://cdn.magicpatterns.com/uploads/siv1okUNAkxhCZsd7Pwxbr/35677327-8D71-4B0C-8305-E02C836CAB8C_2.jpg',
    gallery: [
      'https://cdn.magicpatterns.com/uploads/uUGsKDQ9ZuyruUBxqGxjuF/749403242_17925808158367125_8423757437317615676_n..jpg',
      'https://cdn.magicpatterns.com/uploads/x9G91wUGgW4PeT1pdNuVpb/551860788_17884842120367125_8041553542335615803_n..jpg',
      'https://cdn.magicpatterns.com/uploads/fV4bcFArGpq3Z9HPPZwC5X/586983853_17892207624367125_8566069967174582859_n..jpg',
    ],
    address: 'Storgatan 12, 352 31 Växjö',
    hours: [
      { day: 'Mån–Fre', time: '08.00–17.00' },
      { day: 'Lör', time: '10.00–15.00' },
      { day: 'Sön', time: 'Stängt' },
    ],
    price: 2,
    stamp: 'MÅSTE TESTAS',
    action: 'bestall',
    instagram: '@lilla.verket',
    email: 'hej@lillaverket.se',
  },
  {
    slug: 'top-notch-bakery',
    name: 'TOP NOTCH BAKERY',
    city: 'Växjö',
    kind: 'fika',
    tags: ['Bageri', 'Frukost', 'Fika'],
    blurb: 'Frukosten som får folk att komma i tid till morgonmötet. Och fikat som gör att de stannar.',
    description: [
      'Top Notch Bakery är ett hantverksbageri där allt börjar med riktigt bra råvaror och tid. Bröd, bullar och bakverk görs från grunden — varje dag.',
      'För företag och fester har de färdiga frukostpaket, frallor, caesarsallader, en lång rad bakverk och tårtor på beställning. Allt i samma leverans.',
    ],
    weLike: [
      'Stora frukostpaketet. Ingefärsshoten väcker även de som inte druckit kaffe än.',
      'Att bakverken har namn som Peanut Prince och Let\u2019s Go Nuts.',
      'Veckans bakelse. Man vet aldrig vad det blir, och det är poängen.',
    ],
    image:
      'https://cdn.magicpatterns.com/uploads/caju5vU6ZxpU7hP5vvMVTb/598180901_18089042921511062_8692022983549048154_n..jpg',
    gallery: [
      'https://cdn.magicpatterns.com/uploads/foff1feghzmgrktPEHa3kJ/479502353_18054317555511062_1164978928810660445_n..jpg',
      'https://cdn.magicpatterns.com/uploads/tz597xEfsMcFCP8sLSvn6Q/511454343_18069234947511062_5082887141267518271_n..webp',
      'https://cdn.magicpatterns.com/uploads/rYWb74tCJKh1KKKYkCV9zi/475254364_18052295996511062_1534074201207197451_n..jpg',
    ],
    address: 'Växjö',
    hours: [
      { day: 'Mån–Fre', time: '07.00–17.00' },
      { day: 'Lör', time: '09.00–15.00' },
      { day: 'Sön', time: 'Stängt' },
    ],
    price: 2,
    action: 'bestall',
    instagram: '@top.notch.bakery',
    email: 'order@topnotchbakery.se',
  },
];

/* ---------------------------------- Helpers --------------------------------- */

export const SMAKEN_LOGO =
  'https://cdn.magicpatterns.com/uploads/xqH8AbYdWpUuk9dTngEuGD/Black_and_White_Minimalist_Typographical_Gallery_Logo.png';

export const HERO_IMAGE =
  'https://cdn.magicpatterns.com/patterns/generated-images/4d216c6c-f45d-47c3-a6f9-792c17d6d6aa.jpg';

export const JOIN_IMAGE =
  'https://cdn.magicpatterns.com/patterns/generated-images/9647f4d7-1cec-4bbf-95a3-2f0433ad8bb7.jpg';

/** Real things you can order right now — the ticker is a menu, not a mood board. */
export const TICKER = [
  'FIG & PIG',
  'STORA FRUKOSTPAKETET',
  'BASQUE CHEESECAKE',
  'CAESARSALLAD',
  'KANELBULLE',
  'BEET & BLEAT',
  'PEANUT PRINCE',
  'PAIN AU CHOCOLAT',
  'OVERNIGHT OATS',
  'VECKANS BAKELSE',
];

/* --------------------------------- Catering --------------------------------- */

export interface CateringOffer extends CateringItem {
  placeSlug: string;
  placeName: string;
  placeCity: string;
}

/** Every catering item on the platform, flattened with its partner. */
export const ALL_CATERING: CateringOffer[] = PLACES.flatMap((place) =>
  (CATERING[place.slug] ?? []).map((item) => ({
    ...item,
    placeSlug: place.slug,
    placeName: place.name,
    placeCity: String(place.city),
  }))
);

export const cateringFor = (slug: string): CateringOffer[] =>
  ALL_CATERING.filter((item) => item.placeSlug === slug);

/** "1 250 kr" — whole kronor, for prices incl. VAT. */
export const formatPrice = (value: number): string =>
  `${new Intl.NumberFormat('sv-SE', { maximumFractionDigits: 0 }).format(Math.round(value))} kr`;

/** "49,06 kr" — two decimals, for excl.-VAT figures. */
export const formatPriceExact = (value: number): string =>
  `${new Intl.NumberFormat('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value)} kr`;

/** The VAT part of a price that includes VAT. */
export const vatPart = (priceInclVat: number): number => priceInclVat - exVat(priceInclVat);

export const VAT_LABEL = `${Math.round(VAT_RATE * 100)} %`;

export const kindLabel = (kind: Kind): string => KINDS.find((k) => k.id === kind)?.label ?? '';

export const placesByKind = (kind: Kind): Place[] => PLACES.filter((p) => p.kind === kind);
